import { StyleSheet, css } from 'aphrodite'
import React from 'react'

interface HeroSectionProps {
  subtitle: string
  title: string
  description: string
  imageSrc: string
  imageAlt: string
  leftImageSrc?: string
  leftImageAlt?: string
}

export default function HeroSection({
  subtitle,
  title,
  description,
  imageSrc,
  imageAlt,
  leftImageSrc,
  leftImageAlt,
}: HeroSectionProps) {
  const hasTwoImages = Boolean(leftImageSrc)

  return (
    <section className={css(styles.section)}>
      <div className={css(styles.textContainer)}>
        <h2 className={css(styles.subtitle)}>{subtitle}</h2>
        <h1 className={css(styles.title)}>{title}</h1>
        <p className={css(styles.description)}>{description}</p>
      </div>
      <div
        className={css(
          hasTwoImages ? styles.imageContainer : styles.singleImageContainer,
        )}
      >
        {leftImageSrc && (
          <div className={css(styles.leftImageWrapper)}>
            <img
              src={leftImageSrc}
              alt={leftImageAlt || 'Left hero image'}
              className={css(styles.leftImage)}
            />
          </div>
        )}
        <div
          className={css(
            hasTwoImages ? styles.rightImageWrapper : styles.singleImageWrapper,
          )}
        >
          <img
            src={imageSrc}
            alt={imageAlt}
            className={css(
              hasTwoImages ? styles.heroImage : styles.singleHeroImage,
            )}
          />
        </div>
      </div>
    </section>
  )
}

const styles = StyleSheet.create({
  section: {
    background: '#6DC3FFB2',
    padding: '32px 0.75rem',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    gap: '24px',
    '@media (min-width: 480px)': {
      padding: '40px 1rem',
      gap: '28px',
    },
    '@media (min-width: 768px)': {
      background:
        'linear-gradient(to bottom, #6DC3FFB2 0%, #6DC3FFB2 50%, #ffffff 50%, #ffffff 100%)',
      padding: '48px 2rem',
      gap: '32px',
    },
    '@media (min-width: 1024px)': {
      padding: '64px 3rem',
      gap: '40px',
    },
  },
  textContainer: {
    maxWidth: '100%',
    padding: '0 0.5rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    '@media (min-width: 480px)': {
      padding: '0 1rem',
      gap: '16px',
    },
    '@media (min-width: 768px)': {
      maxWidth: '700px',
      padding: '0',
      gap: '20px',
    },
    '@media (min-width: 1024px)': {
      maxWidth: '900px',
      gap: '24px',
    },
  },
  subtitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1F3D61',
    letterSpacing: '0.5px',
    margin: 0,
    '@media (min-width: 480px)': {
      fontSize: '15px',
      letterSpacing: '0.75px',
    },
    '@media (min-width: 768px)': {
      fontSize: '18px',
      letterSpacing: '1px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '20px',
    },
  },
  title: {
    fontSize: '24px',
    fontWeight: 400,
    fontFamily: '"Anton", sans-serif',
    color: '#1F3D61',
    lineHeight: '1.1',
    margin: 0,
    '@media (min-width: 480px)': {
      fontSize: '28px',
    },
    '@media (min-width: 768px)': {
      fontSize: '48px',
      lineHeight: '1.2',
    },
    '@media (min-width: 1024px)': {
      fontSize: '60px',
    },
  },
  description: {
    fontSize: '13px',
    color: '#4B5563',
    lineHeight: '1.4',
    maxWidth: '100%',
    margin: 0,
    fontWeight: 500,
    '@media (min-width: 480px)': {
      fontSize: '14px',
      lineHeight: '1.5',
    },
    '@media (min-width: 768px)': {
      fontSize: '15px',
      maxWidth: '600px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '16px',
      maxWidth: '800px',
    },
  },
  imageContainer: {
    width: '100%',
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 0.5rem',
    '@media (min-width: 480px)': {
      gap: '16px',
      padding: '0 1rem',
    },
    '@media (min-width: 768px)': {
      flexDirection: 'row',
      maxWidth: 800,
      gap: '20px',
      padding: '0',
    },
    '@media (min-width: 1024px)': {
      gap: '24px',
    },
  },
  leftImageWrapper: {
    width: '100%',
    maxWidth: '280px',
    '@media (min-width: 480px)': {
      maxWidth: '320px',
    },
    '@media (min-width: 768px)': {
      flex: '1',
      maxWidth: '50%',
    },
  },
  leftImage: {
    width: '100%',
    aspectRatio: '4/3',
    borderRadius: '16px',
    display: 'block',
    objectFit: 'cover',
    border: '1px solid #1F3D61',
    '@media (min-width: 480px)': {
      borderRadius: '20px',
    },
    '@media (min-width: 768px)': {
      aspectRatio: '1/1',
      borderRadius: '32px',
    },
  },
  rightImageWrapper: {
    width: '100%',
    maxWidth: '280px',
    '@media (min-width: 480px)': {
      maxWidth: '320px',
    },
    '@media (min-width: 768px)': {
      flex: '1',
      maxWidth: '50%',
    },
  },
  heroImage: {
    width: '100%',
    aspectRatio: '4/3',
    borderRadius: '16px',
    display: 'block',
    objectFit: 'cover',
    border: '1px solid #1F3D61',
    objectPosition: 'center',
    '@media (min-width: 480px)': {
      borderRadius: '20px',
    },
    '@media (min-width: 768px)': {
      aspectRatio: '1/1',
      borderRadius: '32px',
      objectPosition: 'right',
    },
  },
  singleImageContainer: {
    width: '100%',
    maxWidth: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 0.5rem',
    '@media (min-width: 480px)': {
      padding: '0 1rem',
    },
    '@media (min-width: 768px)': {
      maxWidth: '700px',
      padding: '0',
    },
    '@media (min-width: 1024px)': {
      maxWidth: '900px',
    },
  },
  singleImageWrapper: {
    width: '100%',
    maxWidth: '320px',
    '@media (min-width: 480px)': {
      maxWidth: '400px',
    },
    '@media (min-width: 768px)': {
      maxWidth: '100%',
    },
  },
  singleHeroImage: {
    width: '100%',
    height: 'auto',
    aspectRatio: '4/3',
    borderRadius: '16px',
    display: 'block',
    objectFit: 'cover',
    border: '1px solid #1F3D61',
    '@media (min-width: 480px)': {
      borderRadius: '20px',
    },
    '@media (min-width: 768px)': {
      aspectRatio: 'auto',
      borderRadius: '32px',
      border: 'none',
    },
  },
})
